/**
 * Budget Table Component
 *
 * Main table component for budget phases and line items
 * Features: expandable phases, search, filters, pagination, CRUD actions
 *
 * Design specs from Figma:
 * - Table wrapper: bg #15203C, rounded-[20px], p-[30px]
 * - Header: bg #0C1427, 50px height, 14px medium text #E7E7E7
 * - 7 columns: checkbox, item, orç.previsto, orç.executado, orç.aprovado, percentual, ações
 * - Mobile: convert to card list at <768px
 */

import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { cn } from "@/lib/class-control";
import { Button } from "@/components/ui/button";
import { BudgetPhase, BudgetTableFilters } from "@/types/orcamentos.interface";
import { getPhaseColor } from "@/services/orcamentos/mock-data";
import { BudgetTablePhaseHeader } from "./budget-table-phase-header";
import { BudgetTableTopicRow } from "./budget-table-topic-row";
import { BudgetTableLineRow } from "./budget-table-line-row";
import { BudgetTableAddButtonRow } from "./budget-table-add-button-row";
import { BudgetTableFilterDialog } from "./budget-table-filter-dialog";

interface BudgetTableProps {
  /** Budget phases data */
  data: BudgetPhase[];
  /** View line item callback */
  onViewItem: (phaseId: string, topicId: string, itemId: string) => void;
  /** Edit line item callback */
  onEditItem: (phaseId: string, topicId: string, itemId: string) => void;
  /** Delete line item callback */
  onDeleteItem: (phaseId: string, topicId: string, itemId: string) => void;
  /** Add topic to phase callback */
  onAddTopic: (phaseId: string) => void;
  /** Add line item to topic callback */
  onAddLineItem: (phaseId: string, topicId: string) => void;
  /** Novo lançamento callback */
  onNovoLancamento: () => void;
}

export function BudgetTable({
  data,
  onViewItem,
  onEditItem,
  onDeleteItem,
  onAddTopic,
  onAddLineItem,
  onNovoLancamento,
}: BudgetTableProps) {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<BudgetTableFilters>({
    selectedPhases: [],
    dateFrom: "",
    dateTo: "",
    valueMin: 0,
    valueMax: Infinity,
  });

  /**
   * Toggle phase checkbox (select all items in phase)
   */
  const togglePhaseSelection = (phaseId: string) => {
    const phase = data.find((p) => p.id === phaseId);
    if (!phase) return;

    const phaseItemIds = phase.topics.flatMap((topic) =>
      topic.lineItems.map((item) => item.id)
    );
    const allSelected = phaseItemIds.every((id) => selectedItems.has(id));

    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        phaseItemIds.forEach((id) => next.delete(id));
      } else {
        phaseItemIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  /**
   * Toggle topic checkbox (select all items in topic)
   */
  const toggleTopicSelection = (phaseId: string, topicId: string) => {
    const phase = data.find((p) => p.id === phaseId);
    if (!phase) return;

    const topic = phase.topics.find((t) => t.id === topicId);
    if (!topic) return;

    const topicItemIds = topic.lineItems.map((item) => item.id);
    const allSelected = topicItemIds.every((id) => selectedItems.has(id));

    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        topicItemIds.forEach((id) => next.delete(id));
      } else {
        topicItemIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  /**
   * Toggle individual item checkbox
   */
  const toggleItemSelection = (itemId: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  /**
   * Filter and search data across 3 levels
   */
  const filteredData = useMemo(() => {
    let result = data;

    // Phase filter
    if (filters.selectedPhases.length > 0) {
      result = result.filter((phase) =>
        filters.selectedPhases.includes(phase.phaseName)
      );
    }

    // Search filter (searches across phases, topics and line items)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result
        .map((phase) => ({
          ...phase,
          topics: phase.topics
            .map((topic) => ({
              ...topic,
              lineItems: topic.lineItems.filter((item) =>
                item.name.toLowerCase().includes(term)
              ),
            }))
            .filter(
              (topic) =>
                topic.name.toLowerCase().includes(term) ||
                topic.lineItems.length > 0
            ),
        }))
        .filter(
          (phase) =>
            phase.phaseName.toLowerCase().includes(term) ||
            phase.topics.length > 0
        );
    }

    // Value filter
    if (filters.valueMin > 0 || filters.valueMax < Infinity) {
      result = result.map((phase) => ({
        ...phase,
        topics: phase.topics.map((topic) => ({
          ...topic,
          lineItems: topic.lineItems.filter(
            (item) =>
              item.value >= filters.valueMin && item.value <= filters.valueMax
          ),
        })),
      }));
    }

    return result;
  }, [data, filters, searchTerm]);

  /**
   * Pagination
   */
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  /**
   * Change page
   */
  const changePage = (delta: number) => {
    setPage((p) => Math.min(totalPages, Math.max(1, p + delta)));
  };

  /**
   * Apply filters
   */
  const handleApplyFilters = (newFilters: BudgetTableFilters) => {
    setFilters(newFilters);
    setFilterDialogOpen(false);
    setPage(1);
  };

  /**
   * Format currency to Brazilian Real
   */
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <>
      {/* Search and Filter Toolbar */}
      <div className="flex flex-col md:flex-row-reverse gap-4 items-stretch md:items-center mb-[24px]">
        <Button
          size="sm"
          variant="secondary"
          onClick={onNovoLancamento}
        >
          <Plus size={24} />
          Novo Subitem
        </Button>

        <ButtonFilter
          positionIcon="end"
          onClick={() => setFilterDialogOpen(true)}
          className="w-28 text-sm text-white justify-around"
          classNameIcon="text-purple-500"
        />

        <InputSearch
          positionIcon="end"
          value={searchTerm}
          onChange={setSearchTerm}
          classNameContainer="bg-transparent"
        />
      </div>

      {/* Table - Desktop */}
      <div className="hidden md:block bg-[#15203C] rounded-[20px] p-[30px]">
        <table className="w-full">
          <thead className="bg-[#0C1427] h-[50px]">
            <tr>
              <th className="w-[80px] text-center text-[14px] font-bold text-[#F6F6F6]">
                <div className="flex items-center justify-center">Nº</div>
              </th>
              <th className="px-[16px] text-left text-[14px] font-bold text-[#F6F6F6]">
                Descrição do Item
              </th>
              <th className="w-[160px] px-[16px] text-center text-[14px] font-bold text-[#F6F6F6]">
                Última atualização
              </th>
              <th className="w-[180px] px-[16px] text-right text-[14px] font-bold text-[#F6F6F6]">
                Orçamento Previsto
              </th>
              <th className="w-[180px] px-[16px] text-right text-[14px] font-bold text-[#F6F6F6]">
                Orçamento Executado
              </th>
              <th className="w-[100px] px-[16px] text-right text-[14px] font-bold text-[#F6F6F6]">
                % Executado
              </th>
              <th className="w-[100px] px-[16px] text-right text-[14px] font-bold text-[#F6F6F6]">
                Ações
              </th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.map((phase) => {
              const phaseItemIds = phase.topics.flatMap((topic) =>
                topic.lineItems.map((item) => item.id)
              );
              const allSelected =
                phaseItemIds.length > 0 &&
                phaseItemIds.every((id) => selectedItems.has(id));

              // Calculate phase totals
              const totalExecutado = phase.topics.reduce(
                (sum, topic) =>
                  sum +
                  topic.lineItems.reduce(
                    (topicSum, item) => topicSum + item.value,
                    0
                  ),
                0
              );

              return (
                <React.Fragment key={phase.id}>
                  {/* Phase Header */}
                  <BudgetTablePhaseHeader
                    phaseName={phase.phaseName}
                    phaseNumber={phase.id}
                    phaseColor={getPhaseColor(phase.phaseName)}
                    isExpanded={true}
                    onToggle={() => { }}
                    totalExecutado={totalExecutado}
                    totalAprovado={phase.orcamentoAprovado}
                    checked={allSelected}
                    onCheckChange={() => togglePhaseSelection(phase.id)}
                  />

                  {/* Topics */}
                  {phase.topics.map((topic, topicIndex) => {
                    const topicItemIds = topic.lineItems.map((item) => item.id);
                    const allTopicItemsSelected =
                      topicItemIds.length > 0 &&
                      topicItemIds.every((id) => selectedItems.has(id));

                    // Calculate topic totals
                    const topicTotalExecutado = topic.lineItems.reduce(
                      (sum, item) => sum + item.value,
                      0
                    );

                    return (
                      <React.Fragment key={topic.id}>
                        {/* Topic Row */}
                        <BudgetTableTopicRow
                          topicName={topic.name}
                          topicNumber={`${phase.id}.${topicIndex + 1}`}
                          phaseColor={getPhaseColor(phase.phaseName)}
                          isExpanded={true}
                          onToggle={() => { }}
                          totalExecutado={topicTotalExecutado}
                          totalAprovado={topic.orcamentoAprovado}
                          percentual={topic.percentual}
                          checked={allTopicItemsSelected}
                          onCheckChange={() =>
                            toggleTopicSelection(phase.id, topic.id)
                          }
                          onView={() => onViewItem(phase.id, "", topic.id)}
                          onEdit={() => onEditItem(phase.id, "", topic.id)}
                          onDelete={() => onDeleteItem(phase.id, "", topic.id)}
                        />

                        {/* Line Items (Payments) */}
                        {topic.lineItems.map((item, itemIndex) => (
                          <BudgetTableLineRow
                            key={item.id}
                            item={item}
                            // We don't wire sub-item callbacks to payments for now
                            onView={() => { }}
                            onEdit={() => { }}
                            onDelete={() => { }}
                            isEven={itemIndex % 2 === 0}
                            checked={selectedItems.has(item.id)}
                            onCheckChange={() => toggleItemSelection(item.id)}
                          />
                        ))}

                      </React.Fragment>
                    );
                  })}

                  {/* Add Buttons at the end of Phase */}
                  <BudgetTableAddButtonRow
                    buttons={[
                      { text: "+ Sub-item", onClick: () => onAddTopic(phase.id) },
                      { text: "+ Pagamento", onClick: () => onAddLineItem(phase.id, "") }
                    ]}
                    phaseColor={getPhaseColor(phase.phaseName)}
                  />
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-end gap-2">
          <ChevronLeft
            className={cn(
              page === 1
                ? "text-gray-500 cursor-default"
                : "text-[#7846FF] cursor-pointer"
            )}
            onClick={() => {
              if (page > 1) changePage(-1);
            }}
          />

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .slice(0, 5)
            .map((i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={cn(
                  "h-8 w-8 rounded-full text-sm",
                  page === i
                    ? "bg-[#7846FF] text-white"
                    : "text-gray-300 hover:bg-white/10"
                )}
              >
                {i}
              </button>
            ))}

          <ChevronRight
            className={cn(
              page === totalPages
                ? "cursor-default text-gray-500"
                : "cursor-pointer text-[#7846FF]"
            )}
            onClick={() => {
              if (page !== totalPages) changePage(1);
            }}
          />
        </div>
      </div>

      {/* Mobile Card List */}
      <div className="md:hidden space-y-3">
        {paginatedData.map((phase) => {
          const totalItems = phase.topics.reduce(
            (sum, topic) => sum + topic.lineItems.length,
            0
          );
          return (
            <div
              key={phase.id}
              className="rounded-lg border border-gray-800 bg-[#15203C] p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white text-sm font-medium">
                  <span className="mr-2">{phase.id}</span>
                  {phase.phaseName}
                </h3>
                <span className="text-[#7846FF] text-xs font-semibold">
                  {totalItems} itens
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-300">
                <div>
                  <span className="text-gray-400 block">Executado</span>
                  {formatCurrency(phase.orcamentoExecutado)}
                </div>
                <div>
                  <span className="text-gray-400 block">Aprovado</span>
                  {formatCurrency(phase.orcamentoAprovado)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter Dialog */}
      <BudgetTableFilterDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
      />
    </>
  );
}

// Add React import for Fragment
import React from "react"; import ButtonFilter from "../ui/button-filter";
import InputSearch from "../ui/input-search";

